import type { ReactElement } from 'react';
import React from 'react';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import SystemErrorAlert from 'feature-components/system-error-alert';
import { ascendingOrder } from 'helpers/tanstack-query.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import { useTags } from 'services/assist-api/entities/tag/tags';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import type { TestInstanceResponseMarkWithItems } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import {
  type TestItemWithRubricItems,
  useTestItems
} from 'services/assist-api/entities/test-item/test-items';
import { useTestRubricItems } from 'services/assist-api/entities/test-rubric-item/hooks';
import { type TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';
import { isInitialLoad } from 'utils/check-query-status';
import MarkResponseForm from './mark-response-form';

type Props = {
  assessment: BaseTestInstance;
  response: TestInstanceResponseWithResponseItemsAndMediaObjects;
  rubricId: number;
  canEditMark: boolean;
  canAdministrateMark: boolean;
  currentOrganisationUser: MeCustomerUser;
  currentUserMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  activeMarker?: TestInstanceResponseMarkerWithCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
};

export default function MarkResponseFormLoader({
  assessment,
  response,
  rubricId,
  canEditMark,
  canAdministrateMark,
  currentOrganisationUser,
  currentUserMarker,
  activeMarker,
  mark
}: Props): ReactElement {
  /**
   * Currently we are only fetching test items with a type 'arb-long-answer'.
   * This is because for CIPS writing, these are the only question types with
   * test rubric items and other test items such as those in the
   * "CIPS Writing - Reflection" category should not be marked or displayed.
   *
   * We should also filter to only return test items for the response items that exist on the
   * response. This is because, if there is a test item but not a corresponding
   * response item (for any reason), it is not possible to mark that item and it won't be possible
   * to submit the mark.
   */
  const uniqueTestItemIdsWithResponses = [
    ...new Set(response.responseItems.map((ri) => ri.testItem.id))
  ];
  const testItemsQuery = useTestItems<TestItemWithRubricItems>(
    {
      filters: {
        id: uniqueTestItemIdsWithResponses,
        test: assessment.test.id,
        type: 'arb-long-answer'
      },
      groups: ['testitem-testrubricitems'],
      include: 'testRubricItems'
    },
    { enabled: !!uniqueTestItemIdsWithResponses.length }
  );

  /**
   * Currently we are only fetching the rubric items for the first rubric. This
   * is because for CIPS writing (currently the only test that will use the
   * Mark Response screen), there is expected to only ever be only rubric per test.
   * If we need to mark tests with multiple rubrics, this logic will need to be
   * expanded.
   */
  const testRubricItemsQuery = useTestRubricItems<TestRubricItemWithOptions>(
    {
      filters: { testRubrics: rubricId },
      groups: [
        'testrubricitem-displayName',
        'testrubricitem-testrubricitemoptions',
        'testrubricitemoption-details'
      ],
      include: 'testRubricItemOptions'
    },
    { enabled: !!rubricId }
  );

  const tagsQuery = useTags<BaseTag>(
    {
      filters: { type: 'markingAttentionReason' },
      order: ascendingOrder
    },
    { enabled: true }
  );

  // Todo: discuss with Jarrod if we should move the markItemMarkers and markItemMarkOrganisationUsers requests into individual requests inside the Rubric Item component. See Bryn's comment in MR 1188 for details.
  /**
   * Also fetch the markers for each mark item, which are only used for display purposes if the
   * Mark exists. Since the mark may not exist yet, this query shouldn't be required for us to
   * consider loading the mark response form complete.
   */
  const uniqueMarkItemMarkerIds = [
    ...new Set(mark?.markItems?.map((mi) => mi.testInstanceResponseMarker?.id) || [])
  ];
  const markItemMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { id: uniqueMarkItemMarkerIds },
        groups: ['testinstanceresponsemarker-user'],
        include: 'users'
      },
      { enabled: !!uniqueMarkItemMarkerIds.length }
    );

  // Get the organisation users linked to each mark item marker:
  const markItemMarkerOrganisationUserIds =
    markItemMarkersQuery?.data?.json.map(
      (mkr: TestInstanceResponseMarkerWithCustomerUser) => mkr.user.id
    ) || [];
  const markItemMarkerOrganisationUsersQuery = useCustomerUsers<BaseCustomerUser>(
    { filters: { id: markItemMarkerOrganisationUserIds } },
    { enabled: !!markItemMarkerOrganisationUserIds.length }
  );

  const isLoading = isInitialLoad(testItemsQuery, testRubricItemsQuery, tagsQuery);

  const isError = testItemsQuery.isError || testRubricItemsQuery.isError || tagsQuery.isError;

  const testItems = testItemsQuery.data?.json;
  const allRubricItems = testRubricItemsQuery.data?.json;
  const attentionReasons = tagsQuery.data?.json;

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && isError && (
        <LoadingErrorAlert queries={[testItemsQuery, testRubricItemsQuery, tagsQuery]} />
      )}
      {!isLoading && !isError && (
        <>
          {!testItems && (
            <SystemErrorAlert>
              No test items that could be marked were found, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {!allRubricItems && (
            <SystemErrorAlert>
              No rubric items were found for the rubric, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {!attentionReasons && (
            <SystemErrorAlert>
              No attention reasons were found, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {testItems && allRubricItems && attentionReasons && (
            <MarkResponseForm
              responseId={response.id}
              rubricId={rubricId}
              testItems={testItems}
              allResponseItems={response.responseItems}
              allRubricItems={allRubricItems}
              attentionReasons={attentionReasons}
              canEditMark={canEditMark}
              canAdministrateMark={canAdministrateMark}
              currentOrganisationUser={currentOrganisationUser}
              currentUserMarker={currentUserMarker}
              activeMarker={activeMarker}
              mark={mark}
              markItemMarkers={markItemMarkersQuery.data?.json || []}
              markItemMarkerOrganisationUsers={
                markItemMarkerOrganisationUsersQuery.data?.json || []
              }
            />
          )}
        </>
      )}
    </>
  );
}
